/* ==========================================================================
   FGA Custom Leather Builder
   --------------------------------------------------------------------------
   Posts to the admin portal's /api/submit endpoint per the Storefront
   Integration Spec:
     - enum values UPPERCASE
     - country as ISO alpha-2
     - referenceImages appended under the SAME repeated key
     - no client-side image resizing
     - no manual Content-Type header
     - 400 -> errors{} per field, 500 -> error string

   If no endpoint is configured, the form degrades to Shopify's native
   /contact POST so inquiries are never silently lost.
   ========================================================================== */

(function () {
  'use strict';

  var MAX_FILES = 5;
  var MAX_BYTES = 5 * 1024 * 1024;
  var ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

  var REQUIRED = ['name', 'email', 'country', 'gender', 'productType', 'leatherType', 'color', 'quantity'];

  var LABELS = {
    gender: 'Wear',
    productType: 'Piece',
    leatherType: 'Leather',
    color: 'Colour',
    quantity: 'Units'
  };

  function init(root) {
    if (!root || root.dataset.fgaReady === 'true') return;
    root.dataset.fgaReady = 'true';

    var form = root.querySelector('[data-fga-form]');
    var submitBtn = root.querySelector('[data-fga-submit]');
    var statusEl = root.querySelector('[data-fga-status]');
    var doneEl = root.querySelector('[data-fga-done]');
    var buildEl = root.querySelector('[data-fga-build]');
    var refEl = root.querySelector('[data-fga-ref]');
    var fileInput = root.querySelector('[data-fga-files]');
    var dropEl = root.querySelector('[data-fga-drop]');
    var thumbsEl = root.querySelector('[data-fga-thumbs]');
    var countEl = root.querySelector('[data-fga-count]');
    var countFill = root.querySelector('[data-fga-count-fill]');
    var chipEl = root.querySelector('[data-fga-chip]');
    var barEl = root.querySelector('[data-fga-bar]');
    var progressLabel = root.querySelector('[data-fga-progress-label]');

    if (!form) return;

    var ENDPOINT = (root.dataset.endpoint || '').trim();
    var SOURCE_PAGE = (root.dataset.sourcePage || '').trim();

    var files = [];

    /* ── Helpers ───────────────────────────────────────────────── */

    function val(name) {
      var el = form.elements[name];
      if (!el) return '';
      if (el instanceof RadioNodeList || (el.length && !el.tagName)) {
        return (el.value || '').trim();
      }
      return (el.value || '').trim();
    }

    function fieldWrap(name) {
      return root.querySelector('[data-field="' + name + '"]');
    }

    function setError(name, message) {
      var box = root.querySelector('[data-error-for="' + name + '"]');
      if (box) {
        box.textContent = message;
        box.classList.toggle('is-shown', Boolean(message));
      }
      var wrap = fieldWrap(name);
      if (wrap) wrap.classList.toggle('fga-field--invalid', Boolean(message));
    }

    function clearErrors() {
      root.querySelectorAll('[data-error-for]').forEach(function (el) {
        el.textContent = '';
        el.classList.remove('is-shown');
      });
      root.querySelectorAll('[data-field]').forEach(function (el) {
        el.classList.remove('fga-field--invalid');
      });
      setStatus('', null);
    }

    function setStatus(message, kind) {
      if (!statusEl) return;
      statusEl.textContent = message || '';
      statusEl.className = 'fga-status' + (kind ? ' fga-status--' + kind : '');
      statusEl.classList.toggle('is-shown', Boolean(message));
    }

    /* ── Docket ────────────────────────────────────────────────── */

    function docketSet(key, text) {
      var el = root.querySelector('[data-docket="' + key + '"]');
      if (!el) return;
      var empty = !text;
      el.textContent = empty ? 'Not set' : text;
      el.setAttribute('data-empty', empty ? 'true' : 'false');
    }

    function checkedLabel(name) {
      var input = form.querySelector('input[name="' + name + '"]:checked');
      if (!input) return '';
      var face = input.nextElementSibling;
      var nameEl = face ? face.querySelector('.fga-opt__name') : null;
      return nameEl ? nameEl.textContent.trim() : input.value;
    }

    function refreshDocket() {
      var gender = checkedLabel('gender');
      var product = checkedLabel('productType');
      var leather = checkedLabel('leatherType');

      if (val('productType') === 'OTHER' && val('productTypeOther')) {
        product = val('productTypeOther');
      }
      if (val('leatherType') === 'OTHER' && val('leatherTypeOther')) {
        leather = val('leatherTypeOther');
      }

      docketSet('gender', gender);
      docketSet('productType', product);
      docketSet('leatherType', leather);
      docketSet('color', val('color'));
      docketSet('quantity', val('quantity'));

      var imgCount = files.length;
      docketSet('images', imgCount ? imgCount + (imgCount === 1 ? ' file' : ' files') : '');

      var cur = val('budgetCurrency');
      var min = val('budgetMin');
      var max = val('budgetMax');
      var budget = '';
      if (min || max) {
        budget = (cur ? cur + ' ' : '') + (min || '?') + '–' + (max || '?');
      }
      docketSet('budget', budget);

      refreshProgress();
    }

    function refreshProgress() {
      var done = 0;
      REQUIRED.forEach(function (k) {
        if (val(k)) done += 1;
      });
      var pct = Math.round((done / REQUIRED.length) * 100);
      if (barEl) barEl.style.width = pct + '%';
      if (progressLabel) {
        progressLabel.textContent = done + ' of ' + REQUIRED.length + ' required details complete';
      }
    }

    /* ── Colour ────────────────────────────────────────────────── */

    var probe = document.createElement('span');

    function resolveColour(text) {
      if (!text) return null;
      probe.style.color = '';
      probe.style.color = text.toLowerCase().replace(/\s+/g, '');
      return probe.style.color ? probe.style.color : null;
    }

    function applyChoiceColour(hex) {
      root.style.setProperty('--fga-choice', hex || '#9a9a9a');
      if (chipEl) chipEl.style.background = hex || '#9a9a9a';
    }

    var colourInput = form.elements['color'];
    var swatches = root.querySelectorAll('[data-swatch]');

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var name = btn.getAttribute('data-swatch');
        var hex = btn.getAttribute('data-hex');
        if (colourInput) colourInput.value = name;
        swatches.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        applyChoiceColour(hex);
        setError('color', '');
        refreshDocket();
      });
    });

    if (colourInput) {
      colourInput.addEventListener('input', function () {
        var typed = colourInput.value.trim();
        var match = null;
        swatches.forEach(function (b) {
          if (b.getAttribute('data-swatch').toLowerCase() === typed.toLowerCase()) {
            match = b.getAttribute('data-hex');
          }
          b.classList.remove('is-active');
        });
        applyChoiceColour(match || resolveColour(typed));
        refreshDocket();
      });
    }

    /* ── "Other" toggles ───────────────────────────────────────── */

    root.querySelectorAll('[data-other-for]').forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.getAttribute('data-other-for');
        var panel = root.querySelector('[data-other-panel="' + key + '"]');
        if (!panel) return;
        var show = input.value === 'OTHER' && input.checked;
        panel.hidden = !show;
        if (!show) {
          var f = panel.querySelector('input');
          if (f) f.value = '';
          setError(key, '');
        } else {
          var focusable = panel.querySelector('input');
          if (focusable) focusable.focus();
        }
        refreshDocket();
      });
    });

    /* ── Quantity stepper ──────────────────────────────────────── */

    var qtyInput = form.elements['quantity'];

    root.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(btn.getAttribute('data-qty-step'), 10);
        var current = parseInt(qtyInput.value, 10);
        if (isNaN(current)) current = 1;
        var next = Math.min(1000, Math.max(1, current + step));
        qtyInput.value = next;
        refreshDocket();
      });
    });

    /* ── Files ─────────────────────────────────────────────────── */

    function addFiles(list) {
      var rejected = '';
      Array.prototype.forEach.call(list, function (file) {
        if (files.length >= MAX_FILES) {
          rejected = 'Upload at most ' + MAX_FILES + ' reference images.';
          return;
        }
        if (ACCEPTED.indexOf(file.type) === -1) {
          rejected = '"' + file.name + '" is not supported. Use JPG, JPEG, PNG or WEBP.';
          return;
        }
        if (file.size > MAX_BYTES) {
          rejected = '"' + file.name + '" is larger than 5 MB.';
          return;
        }
        files.push(file);
      });
      setError('referenceImages', rejected);
      renderThumbs();
      refreshDocket();
    }

    /**
     * @param {number} bytes
     * @returns {string}
     */
    function humanSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    function refreshCount() {
      var full = files.length >= MAX_FILES;

      if (countEl) {
        countEl.textContent = files.length + ' of ' + MAX_FILES + ' added';
      }
      if (countFill) {
        countFill.style.width = (files.length / MAX_FILES) * 100 + '%';
      }
      if (dropEl) {
        dropEl.classList.toggle('is-full', full);
      }
      if (fileInput) {
        // Disabling at the cap stops the picker opening on a zone that cannot
        // accept anything, rather than letting the user pick and be rejected.
        fileInput.disabled = full;
      }

      var lead = root.querySelector('.fga-drop__lead');
      if (lead) {
        lead.innerHTML = full
          ? 'Maximum of ' + MAX_FILES + ' images added'
          : 'Drop images here or <u>browse</u>';
      }
    }

    function renderThumbs() {
      if (!thumbsEl) return;
      thumbsEl.innerHTML = '';
      files.forEach(function (file, index) {
        var cell = document.createElement('li');
        cell.className = 'fga-thumb';

        var img = document.createElement('img');
        var url = URL.createObjectURL(file);
        img.src = url;
        img.alt = '';
        img.addEventListener('load', function () { URL.revokeObjectURL(url); });

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'fga-thumb__x';
        remove.innerHTML = '&times;';
        remove.setAttribute('aria-label', 'Remove ' + file.name);
        remove.addEventListener('click', function () {
          files.splice(index, 1);
          setError('referenceImages', '');
          renderThumbs();
          refreshDocket();
        });

        var foot = document.createElement('span');
        foot.className = 'fga-thumb__foot';
        foot.textContent = humanSize(file.size);
        foot.title = file.name;

        cell.appendChild(img);
        cell.appendChild(remove);
        cell.appendChild(foot);
        thumbsEl.appendChild(cell);
      });
      refreshCount();
    }

    if (fileInput) {
      // Covers both the picker and a native drop onto the overlaid input.
      fileInput.addEventListener('change', function () {
        addFiles(fileInput.files);
        fileInput.value = '';
      });
    }

    if (dropEl) {
      // Styling only. The input sits on top at inset:0 and handles the drop
      // itself, so preventing the default here would break that and adding
      // files here as well would add every dropped file twice.
      ['dragenter', 'dragover'].forEach(function (type) {
        dropEl.addEventListener(type, function () {
          if (files.length < MAX_FILES) dropEl.classList.add('is-over');
        });
      });
      ['dragleave', 'drop'].forEach(function (type) {
        dropEl.addEventListener(type, function (e) {
          if (type === 'dragleave' && e.relatedTarget && dropEl.contains(e.relatedTarget)) return;
          dropEl.classList.remove('is-over');
        });
      });
    }

    /* ── Live docket updates ───────────────────────────────────── */

    form.addEventListener('change', refreshDocket);
    form.addEventListener('input', refreshDocket);

    /* ── Client-side validation (UX only — server is authority) ── */

    var MESSAGES = {
      name: 'Full name is required',
      email: 'Email address is required',
      country: 'Country is required',
      gender: 'Select Men, Women or Unisex',
      productType: 'Select a product type',
      leatherType: 'Select a leather type',
      color: 'Colour is required',
      quantity: 'Quantity is required'
    };

    function validate() {
      var ok = true;
      var firstBad = null;

      REQUIRED.forEach(function (key) {
        if (!val(key)) {
          setError(key, MESSAGES[key]);
          if (!firstBad) firstBad = key;
          ok = false;
        }
      });

      var email = val('email');
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('email', 'Enter a valid email address');
        if (!firstBad) firstBad = 'email';
        ok = false;
      }

      if (val('productType') === 'OTHER' && !val('productTypeOther')) {
        setError('productTypeOther', 'Describe the product type');
        if (!firstBad) firstBad = 'productTypeOther';
        ok = false;
      }

      if (val('leatherType') === 'OTHER' && !val('leatherTypeOther')) {
        setError('leatherTypeOther', 'Describe the leather type');
        if (!firstBad) firstBad = 'leatherTypeOther';
        ok = false;
      }

      var qty = parseInt(val('quantity').replace(/[,\s]/g, ''), 10);
      if (val('quantity') && (isNaN(qty) || qty < 1)) {
        setError('quantity', 'Quantity must be a whole number of at least 1');
        if (!firstBad) firstBad = 'quantity';
        ok = false;
      } else if (qty > 1000) {
        setError('quantity', 'Quantity cannot exceed 1000');
        if (!firstBad) firstBad = 'quantity';
        ok = false;
      }

      var hasAmount = val('budgetMin') || val('budgetMax');
      if (hasAmount && !val('budgetCurrency')) {
        setError('budgetCurrency', 'Select a currency for the budget');
        if (!firstBad) firstBad = 'budgetCurrency';
        ok = false;
      }
      if (val('budgetMin') && val('budgetMax')) {
        if (parseInt(val('budgetMax'), 10) < parseInt(val('budgetMin'), 10)) {
          setError('budgetMax', 'Maximum budget must be greater than the minimum');
          if (!firstBad) firstBad = 'budgetMax';
          ok = false;
        }
      }

      if (firstBad) {
        var wrap = fieldWrap(firstBad);
        if (wrap && wrap.scrollIntoView) {
          wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        var focusTarget = wrap ? wrap.querySelector('input, select, textarea') : null;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }

      return ok;
    }

    /* ── Payload ───────────────────────────────────────────────── */

    function buildPayload() {
      var fd = new FormData();

      REQUIRED.forEach(function (key) {
        fd.set(key, val(key));
      });

      ['phone', 'designNotes', 'productTypeOther', 'leatherTypeOther',
        'budgetMin', 'budgetMax', 'budgetCurrency'].forEach(function (key) {
        var v = val(key);
        if (v) fd.set(key, v);
      });

      if (SOURCE_PAGE) fd.set('sourcePage', SOURCE_PAGE);

      // Same key, once per file. Never resized client-side.
      files.forEach(function (file) {
        fd.append('referenceImages', file);
      });

      return fd;
    }

    /* ── Native /contact fallback ──────────────────────────────── */

    function submitNative() {
      var fallback = document.createElement('form');
      fallback.method = 'post';
      fallback.action = '/contact#contact_form';
      fallback.acceptCharset = 'UTF-8';
      fallback.style.display = 'none';

      function hidden(name, value) {
        var i = document.createElement('input');
        i.type = 'hidden';
        i.name = name;
        i.value = value;
        fallback.appendChild(i);
      }

      hidden('form_type', 'contact');
      hidden('utf8', '✓');
      hidden('contact[name]', val('name'));
      hidden('contact[email]', val('email'));
      if (val('phone')) hidden('contact[Phone]', val('phone'));
      hidden('contact[Country]', val('country'));
      hidden('contact[Wear]', checkedLabel('gender'));
      hidden('contact[Product]', val('productType') === 'OTHER' ? val('productTypeOther') : checkedLabel('productType'));
      hidden('contact[Leather]', val('leatherType') === 'OTHER' ? val('leatherTypeOther') : checkedLabel('leatherType'));
      hidden('contact[Colour]', val('color'));
      hidden('contact[Quantity]', val('quantity'));
      if (val('budgetCurrency') || val('budgetMin') || val('budgetMax')) {
        hidden('contact[Budget]', val('budgetCurrency') + ' ' + val('budgetMin') + '-' + val('budgetMax'));
      }
      if (val('designNotes')) hidden('contact[Design notes]', val('designNotes'));
      if (SOURCE_PAGE) hidden('contact[Source page]', SOURCE_PAGE);
      if (files.length) {
        hidden('contact[Reference images]', files.length + ' image(s) selected — not transferable without the portal endpoint. Please email them.');
      }

      document.body.appendChild(fallback);
      fallback.submit();
    }

    /* ── Submit ────────────────────────────────────────────────── */

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors();

      if (!validate()) {
        setStatus('Please check the highlighted details and try again.', 'error');
        return;
      }

      if (!ENDPOINT) {
        setStatus('Sending your request…', 'busy');
        submitNative();
        return;
      }

      submitBtn.disabled = true;
      submitBtn.classList.add('is-busy');
      setStatus('Sending your request…', 'busy');

      fetch(ENDPOINT, { method: 'POST', body: buildPayload() })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          var data = result.data || {};

          if (result.ok && data.ok) {
            if (refEl) refEl.textContent = data.ref || '';
            if (buildEl) buildEl.hidden = true;
            if (doneEl) {
              doneEl.classList.add('is-shown');
              doneEl.setAttribute('tabindex', '-1');
              doneEl.focus({ preventScroll: true });
              doneEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }

          if (data.errors) {
            Object.keys(data.errors).forEach(function (key) {
              setError(key, data.errors[key]);
            });
            setStatus('Please check the highlighted details and try again.', 'error');
            var firstErrorKey = Object.keys(data.errors)[0];
            var wrap = fieldWrap(firstErrorKey);
            if (wrap && wrap.scrollIntoView) {
              wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else {
            setStatus(data.error || 'Something went wrong. Please try again.', 'error');
          }
        })
        .catch(function () {
          setStatus('We could not reach our server. Please check your connection and try again.', 'error');
        })
        .finally(function () {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-busy');
        });
    });

    refreshDocket();
    refreshCount();
  }

  function boot() {
    document.querySelectorAll('[data-fga-designer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections without a page reload.
  document.addEventListener('shopify:section:load', boot);
})();
